import { useEffect, useRef, useState, type ChangeEvent, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
  Stack,
  type PopperProps,
} from '@mui/material';
import type { ButtonProps } from '@mui/material/Button';
import GifBoxOutlinedIcon from '@mui/icons-material/GifBoxOutlined';

import CoreQuillEditor from '../shared/CoreQuillEditor';
import useBoardStore from '../../store/boardStore';
import useUserStore from '../../store/userStore';
import TenorGifDialog from './TenorGifDialog';

const hasIdeaContent = (value: string) =>
  /<img\b[^>]*>/i.test(value) ||
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const hasTextContent = (value: string) =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

const popperModifiers: PopperProps['modifiers'] = [
  {
    name: 'offset',
    options: {
      offset: [0, 14],
    },
  },
];

type AddIdeaPopupProps = {
  canAddCards?: boolean;
  columnId?: string;
  buttonLabel?: string;
  buttonSize?: ButtonProps['size'];
  buttonVariant?: ButtonProps['variant'];
};

const AddIdeaPopup = ({
  canAddCards = true,
  columnId,
  buttonLabel = 'Add a Card',
  buttonSize = 'medium',
  buttonVariant = 'contained',
}: AddIdeaPopupProps) => {
  const addIdea = useBoardStore((state) => state.addIdea);
  const addIdeaImage = useBoardStore((state) => state.addIdeaImage);
  const addIdeaVideo = useBoardStore((state) => state.addIdeaVideo);
  const addIdeaFile = useBoardStore((state) => state.addIdeaFile);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const [ideaEditorValue, setIdeaEditorValue] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isGifDialogOpen, setIsGifDialogOpen] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [attachedImages, setAttachedImages] = useState<File[]>([]);
  const [attachedVideo, setAttachedVideo] = useState<File | null>(null);
  const [attachedPdf, setAttachedPdf] = useState<File | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const popupContentRef = useRef<HTMLDivElement | null>(null);

  const isOpen = Boolean(anchorEl);
  const isBoardLocked = boardDetails?.locked === true;
  const giphyEnabled = boardDetails?.giphyEnabled !== false;
  const canSubmitIdea =
    (hasIdeaContent(ideaEditorValue) ||
      attachedImages.length > 0 ||
      Boolean(attachedVideo) ||
      Boolean(attachedPdf)) &&
    !isUploadingAttachment;
  const enabledUploadTypes = (boardDetails?.uploadTypes ?? '')
    .split(',')
    .map((uploadType) => uploadType.trim())
    .filter(Boolean);
  const canUploadImage = enabledUploadTypes.includes('image');
  const canUploadVideo = enabledUploadTypes.includes('video');
  const canUploadPdf = enabledUploadTypes.includes('pdf');
  const attachmentAccept = [
    canUploadImage ? 'image/*' : null,
    canUploadVideo ? 'video/*' : null,
    canUploadPdf ? 'application/pdf,.pdf' : null,
  ]
    .filter(Boolean)
    .join(',');

  const addIdeaFromEditor = async () => {
    if (!currentUser || !canSubmitIdea) {
      return;
    }

    if (attachedImages.length > 0) {
      setIsUploadingAttachment(true);
      try {
        await addIdeaImage(
          attachedImages,
          hasTextContent(ideaEditorValue) ? ideaEditorValue : undefined,
          { columnId }
        );
      } finally {
        setIsUploadingAttachment(false);
      }
    } else if (attachedVideo) {
      setIsUploadingAttachment(true);
      try {
        await addIdeaVideo(
          attachedVideo,
          hasTextContent(ideaEditorValue) ? ideaEditorValue : undefined,
          { columnId }
        );
      } finally {
        setIsUploadingAttachment(false);
      }
    } else if (attachedPdf) {
      setIsUploadingAttachment(true);
      try {
        await addIdeaFile(
          attachedPdf,
          hasTextContent(ideaEditorValue) ? ideaEditorValue : undefined,
          { columnId }
        );
      } finally {
        setIsUploadingAttachment(false);
      }
    } else {
      await addIdea(ideaEditorValue, { columnId });
    }

    setIdeaEditorValue('');
    setAttachedImages([]);
    setAttachedVideo(null);
    setAttachedPdf(null);
    setAnchorEl(null);
  };

  const openPopup = (event: MouseEvent<HTMLButtonElement>) => {
    if (isBoardLocked || !canAddCards) {
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const closePopup = () => {
    setAnchorEl(null);
    setIdeaEditorValue('');
    setIsGifDialogOpen(false);
    setIsUploadingAttachment(false);
    setAttachedImages([]);
    setAttachedVideo(null);
    setAttachedPdf(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const openGifDialog = () => {
    if (!giphyEnabled) {
      return;
    }

    setIsGifDialogOpen(true);
  };

  const closeGifDialog = () => {
    setIsGifDialogOpen(false);
  };

  const addGifIdea = async (gifUrl: string) => {
    if (!currentUser) {
      return;
    }

    await addIdea(`<div><img src="${gifUrl}" alt="GIF idea" /></div>`, { columnId });
    closePopup();
  };

  const handleAttachmentClick = () => {
    attachmentInputRef.current?.click();
  };

  const attachAttachmentIdea = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0 || !currentUser || isUploadingAttachment) {
      return;
    }

    const selectedVideo = selectedFiles.find((file) => file.type.startsWith('video/')) ?? null;
    const selectedPdf =
      selectedFiles.find(
        (file) => file.type === 'application/pdf' || file.name.trim().toLowerCase().endsWith('.pdf')
      ) ?? null;

    if (selectedVideo) {
      setAttachedImages([]);
      setAttachedPdf(null);
      setAttachedVideo(selectedVideo);
      event.target.value = '';
      return;
    }

    if (selectedPdf) {
      setAttachedImages([]);
      setAttachedVideo(null);
      setAttachedPdf(selectedPdf);
      event.target.value = '';
      return;
    }

    const selectedImages = selectedFiles.filter((file) => file.type.startsWith('image/'));
    if (selectedImages.length > 0) {
      setAttachedVideo(null);
      setAttachedPdf(null);
      setAttachedImages((currentFiles) => [...currentFiles, ...selectedImages]);
    }

    event.target.value = '';
  };

  const clearAttachedImage = (index: number) => {
    setAttachedImages((currentFiles) =>
      currentFiles.filter((_, currentIndex) => currentIndex !== index)
    );
  };

  const clearAttachedVideo = () => {
    setAttachedVideo(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const clearAttachedPdf = () => {
    setAttachedPdf(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const buildAttachmentList = () => {
    const attachments = [];
    if (canUploadImage) {
      attachments.push('images');
    } else if (canUploadVideo) {
      attachments.push('videos');
    } else if (canUploadPdf) {
      attachments.push('PDFs');
    }

    if (attachments.length === 0) {
      return 'No attachments allowed';
    } else if (attachments.length === 1) {
      return `${attachments[0]}`;
    } else {
      return `${attachments.slice(0, -1).join(', ')} or ${attachments[attachments.length - 1]}`;
    }
  };

  const buildAttachmentButtonLabel = () => {
    if (attachedVideo || attachedPdf) {
      return 'Replace attachment';
    }

    if (attachedImages.length > 0) {
      return 'Add more attachments';
    }

    return `Add ${buildAttachmentList()}`;
  };

  useEffect(() => {
    if ((isBoardLocked || !canAddCards) && isOpen) {
      closePopup();
    }
  }, [canAddCards, isBoardLocked, isOpen]);

  useEffect(() => {
    if (!giphyEnabled) {
      setIsGifDialogOpen(false);
    }
  }, [giphyEnabled]);

  useEffect(() => {
    if (!isOpen || isGifDialogOpen) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      popupContentRef.current?.querySelector<HTMLElement>('.ql-editor')?.focus();
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isGifDialogOpen, isOpen]);

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <Button
        variant={buttonVariant}
        size={buttonSize}
        onClick={openPopup}
        disabled={isBoardLocked || !canAddCards}
        sx={{ flex: 1 }}
      >
        {buttonLabel}
      </Button>
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: 1300 }}
        modifiers={popperModifiers}
      >
        <ClickAwayListener onClickAway={closePopup}>
          <Paper
            ref={popupContentRef}
            elevation={8}
            sx={{
              width: { xs: 'calc(100vw - 48px)', sm: 520 },
              maxWidth: '100%',
              p: 2,
            }}
          >
            {!isGifDialogOpen ? (
              <>
                {(canUploadImage || canUploadVideo || canUploadPdf) && (
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      bgcolor: 'transparent',
                      mb: 1.5,
                    }}
                  >
                    <Stack direction="column" alignItems="stretch">
                      {(attachedImages.length > 0 || attachedVideo || attachedPdf) && (
                        <Stack direction="column" gap={1} p={1}>
                          {attachedImages.map((attachedImage, index) => (
                            <Chip
                              key={`${attachedImage.name}-${attachedImage.lastModified}-${index}`}
                              sx={{ fontWeight: 700, px: 2 }}
                              label={attachedImage.name}
                              onDelete={() => clearAttachedImage(index)}
                            />
                          ))}
                          {attachedVideo && (
                            <Chip
                              sx={{ fontWeight: 700, px: 2 }}
                              label={attachedVideo.name}
                              onDelete={clearAttachedVideo}
                            />
                          )}
                          {attachedPdf && (
                            <Chip
                              sx={{ fontWeight: 700, px: 2 }}
                              label={attachedPdf.name}
                              onDelete={clearAttachedPdf}
                            />
                          )}
                        </Stack>
                      )}
                      {(attachedImages.length > 0 || attachedVideo || attachedPdf) && (
                        <Divider sx={{ width: '100%' }} />
                      )}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        alignItems="stretch"
                        justifyContent="center"
                        sx={{ p: 1 }}
                      >
                        <Button
                          variant="text"
                          onClick={handleAttachmentClick}
                          disabled={isUploadingAttachment}
                          sx={{ width: '100%', fontWeight: 700 }}
                        >
                          {buildAttachmentButtonLabel()}
                        </Button>
                      </Stack>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        accept={attachmentAccept}
                        multiple={canUploadImage}
                        hidden
                        onChange={attachAttachmentIdea}
                      />
                    </Stack>
                  </Box>
                )}
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    '.ql-container': {
                      border: 0,
                    },
                    '.ql-editor': {
                      minHeight: 140,
                    },
                  }}
                >
                  <CoreQuillEditor value={ideaEditorValue} onChange={setIdeaEditorValue} />
                </Box>
                <Stack direction="row" justifyContent="space-between" mt={1.5}>
                  <Stack direction="row" spacing={0.5}>
                    {giphyEnabled && (
                      <IconButton
                        size="small"
                        onClick={openGifDialog}
                        aria-label="Add GIF from Tenor"
                        color="primary"
                        disabled={
                          isUploadingAttachment || Boolean(attachedVideo) || Boolean(attachedPdf)
                        }
                      >
                        <GifBoxOutlinedIcon />
                      </IconButton>
                    )}
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button onClick={closePopup} disabled={isUploadingAttachment}>
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={addIdeaFromEditor}
                      disabled={!canSubmitIdea}
                    >
                      {isUploadingAttachment ? 'Uploading...' : 'Add'}
                    </Button>
                  </Stack>
                </Stack>
              </>
            ) : (
              <TenorGifDialog onClose={closeGifDialog} onSelectGif={addGifIdea} />
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default AddIdeaPopup;
